/**
 * RAM — SignatureCard action.
 *
 * Kicks off a signature-card update → SignNow session. The heavy-lifting
 * signing UX lives in RSC; here we stage the intent and navigate the FSR
 * over to the signing workspace with context preloaded.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Banner, Button, Pill } from '@/components/primitives';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useAuth } from '@/contexts/AuthContext';
import { asIso8601 } from '@retail-ops/shared-types';
import type { SessionEventPayload } from '@retail-ops/shared-types';
import type { RamTarget } from '../types';

export interface SignatureCardProps {
  target: RamTarget;
  onDone: () => void;
  onCancel: () => void;
}

type DeliveryMethod = 'inperson-email' | 'inperson-sms' | 'remote-email' | 'remote-sms';

export default function SignatureCard({
  target,
  onDone,
  onCancel,
}: SignatureCardProps): JSX.Element {
  const client = useApiClient();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [method, setMethod] = useState<DeliveryMethod>('inperson-email');
  const [recipient, setRecipient] = useState<string>('');
  const [staging, setStaging] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const canStage = !staging && recipient.trim().length > 3;

  const handleStage = async (): Promise<void> => {
    if (!canStage) return;
    setStaging(true);
    setError(null);
    try {
      const ev: SessionEventPayload = {
        eventType: 'signing-invite-sent',
        eventCategory: 'signing',
        severity: 'info',
        actor: user?.upn ?? 'fsr',
        timestamp: asIso8601(new Date().toISOString()),
        payloadJson: JSON.stringify({
          action: 'signature-card-update',
          cifId: target.cifId,
          acctId: target.acctId,
          method,
          recipient,
        }),
      };
      await client.retail.events.write(ev).catch(() => undefined);

      // Hand off to RSC with context so that module can actually dispatch
      // the SignNow invite. The RSC module owns the /signing/start call.
      const qp = new URLSearchParams({
        cifId: target.cifId,
        doc: 'signature-card',
        method,
      });
      if (target.acctId) qp.set('acctId', target.acctId);
      navigate(`/rsc?${qp.toString()}`);
      onDone();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Unable to stage signing.');
      setStaging(false);
    }
  };

  return (
    <div className="ram-wizard">
      <div className="ram-wizard__header">
        <h2 className="ram-wizard__title">Signature card update</h2>
        <Pill variant="info">CIF {target.cifId}</Pill>
      </div>

      <Banner tone="info">
        Signature card update is dispatched through the Retail Signing Center
        (RSC). After staging here, you will be handed off to the signing
        workspace to complete the SignNow invite.
      </Banner>

      <div className="ram-wizard__fields">
        <label className="ram-search__label">
          Delivery method
          <select
            className="ram-input"
            value={method}
            onChange={(e) => setMethod(e.target.value as DeliveryMethod)}
          >
            <option value="inperson-email">In-person · Email</option>
            <option value="inperson-sms">In-person · SMS</option>
            <option value="remote-email">Remote · Email</option>
            <option value="remote-sms">Remote · SMS</option>
          </select>
        </label>
        <label className="ram-search__label" style={{ gridColumn: '1 / -1' }}>
          Recipient (email or phone)
          <input
            className="ram-input"
            value={recipient}
            onChange={(e) => setRecipient(e.target.value)}
            maxLength={120}
          />
        </label>
      </div>

      {error && <Banner tone="err">{error}</Banner>}

      <div className="ram-wizard__actions">
        <Button variant="ghost" onClick={onCancel} disabled={staging}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleStage} disabled={!canStage}>
          {staging ? 'Staging…' : 'Continue to signing'}
        </Button>
      </div>
    </div>
  );
}
